import { defineStore } from 'pinia'
import { authService, type LoginRequest, type AuthUser } from '@/services/auth'
import { initXsrfToken } from '@/utils/http'
import { userService } from '@/services/users'
import { membershipService, MemberStatus, type MembershipDto } from '@/services/membership'
import type { UserInfo } from '@/services/users/models'

interface AuthState {
  user: AuthUser | null
  loading: boolean
  isAuthenticated: boolean
  membership: MembershipDto | null
  lastMembershipCheck: number
}

// 缓存过期时间（毫秒），例如 5 分钟
const MEMBERSHIP_CACHE_DURATION = 5 * 60 * 1000

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    loading: false,
    isAuthenticated: false,
    membership: null,
    lastMembershipCheck: 0
  }),

  getters: {
    userProfile: (state) => state.user?.profile,
    currentUser: (state) => state.user?.currentUser,
    accessToken: (state) => state.user?.access_token,
    userName: (state) => {
      // 优先使用 currentUser 中的用户名
      if (state.user?.currentUser?.userName) {
        return state.user.currentUser.userName
      }
      // 其次使用 profile 中的用户名
      if (state.user?.profile?.preferred_username) {
        return state.user.profile.preferred_username
      }
      // 最后使用名称或邮箱
      return state.user?.profile?.name || state.user?.currentUser?.email || '未知用户'
    },
    displayName: (state) => {
      // 优先显示完整姓名
      if (state.user?.currentUser?.name || state.user?.currentUser?.surname) {
        const fullName = [state.user.currentUser.name, state.user.currentUser.surname].filter(Boolean).join(' ')
        if (fullName) return fullName
      }
      // 其次显示 profile 中的名称
      if (state.user?.profile?.name) {
        return state.user.profile.name
      }
      // 最后使用用户名
      return (state as any).userName
    },
    email: (state) => state.user?.currentUser?.email || state.user?.profile?.email,
    phoneNumber: (state) => state.user?.currentUser?.phoneNumber,
    isEmailConfirmed: (state) => state.user?.currentUser?.emailVerified || false,
    isPhoneConfirmed: (state) => state.user?.currentUser?.phoneNumberVerified || false,
    roles: (state) => state.user?.currentUser?.roles || [],
    userId: (state) => state.user?.currentUser?.id,
    
    // 会员相关 Getter
    isMember: (state) => {
      if (!state.membership) return false
      // 检查状态是否活跃
      if (state.membership.status !== MemberStatus.Active) return false
      // 检查是否过期
      if (state.membership.expireDate) {
        return new Date(state.membership.expireDate) > new Date()
      }
      return false
    },
    membershipExpireDate: (state) => state.membership?.expireDate
  },

  actions: {
    async initAuth() {
      this.loading = true
      try {
        const user = await authService.getUser()
        if (user) {
          this.user = user
          this.isAuthenticated = true
          // 初始化时获取会员状态
          await this.fetchMembershipStatus()
        } else {
          this.user = null
          this.isAuthenticated = false
          this.clearMembershipState()
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
        this.user = null
        this.isAuthenticated = false
        this.clearMembershipState()
      } finally {
        this.loading = false
      }
    },

    async login(request: LoginRequest) {
      this.loading = true
      try {
        const user = await authService.login(request)
        this.user = user
        this.isAuthenticated = true

        // 登录成功后刷新 XSRF Token（强制刷新以匹配新用户）
        await initXsrfToken(true)
        
        // 登录后获取会员状态
        await this.fetchMembershipStatus()

        return user
      } catch (error) {
        console.error('Login failed:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        authService.logout()
        this.user = null
        this.isAuthenticated = false
        this.clearMembershipState()

        // 退出登录后刷新 XSRF Token（变回匿名用户）
        await initXsrfToken(true)
      } catch (error) {
        console.error('Logout failed:', error)
      }
    },

    async refreshToken() {
      try {
        const user = await authService.refreshToken()
        if (user) {
          this.user = user
          this.isAuthenticated = true

          // Token 刷新成功后更新 XSRF Token
          await initXsrfToken(true)

          return true
        } else {
          this.user = null
          this.isAuthenticated = false
          this.clearMembershipState()
          return false
        }
      } catch (error) {
        console.error('Token refresh failed:', error)
        this.user = null
        this.isAuthenticated = false
        this.clearMembershipState()
        return false
      }
    },

    async refreshUserInfo(): Promise<UserInfo | null> {
      try {
        const userInfo = await userService.getCurrentUser()
        if (userInfo && this.user) {
          this.user.userInfo = userInfo
          return userInfo
        }
        return null
      } catch (error) {
        console.error('Failed to refresh user info:', error)
        return null
      }
    },

    // 强制从服务器获取会员状态
    async fetchMembershipStatus() {
      if (!this.isAuthenticated) return

      try {
        // 使用 getMyMembership 获取完整信息，包括过期时间
        const membership = await membershipService.getMyMembership()
        this.membership = membership
        this.lastMembershipCheck = Date.now()
      } catch (error) {
        console.error('Failed to fetch membership status:', error)
        // 如果获取失败（可能是404表示没开通），则重置状态
        this.membership = null
        this.lastMembershipCheck = Date.now()
      }
    },

    // 检查会员状态（带缓存）
    async checkMembershipStatus() {
      if (!this.isAuthenticated) return false

      const now = Date.now()
      // 如果缓存未过期且有会员信息，直接返回计算属性
      if (this.lastMembershipCheck > 0 && 
          (now - this.lastMembershipCheck < MEMBERSHIP_CACHE_DURATION)) {
        return this.isMember
      }

      // 否则重新获取
      await this.fetchMembershipStatus()
      return this.isMember
    },
    
    // 清除会员状态
    clearMembershipState() {
      this.membership = null
      this.lastMembershipCheck = 0
    }
  }
})
